// The timing readout (slice 15): shown only when the address carries
// `?perf=1`, for whoever tests the board's speed — a person on real
// hardware, or the QA agent reporting a wait. It watches the page's own
// animation frames, so it measures everything the page does, the board
// included, and names the renderer so a software one is never mistaken
// for the table being slow.
import { useEffect, useRef, useState } from 'react';
import { frameStats, isSoftwareRenderer, perfRequested, rendererName, type FrameStats } from './perf';
import { effectsBus } from './effects';
import { animationStats } from './animated';
import { pyramidStats } from './pyramidLayer';

const WINDOW_MS = 5000;

export function PerfReadout({ search = typeof window === 'undefined' ? '' : window.location.search }: { search?: string }) {
  const on = perfRequested(search);
  const [stats, setStats] = useState<FrameStats>({ fps: 0, p95: 0, worst: 0 });
  const [worstEver, setWorstEver] = useState(0);
  const [renderer] = useState(() => (on ? rendererName() : ''));
  const gaps = useRef<Array<{ at: number; gap: number }>>([]);
  const worstRef = useRef(0);
  // Visual effects (issue 1164): how many play now, how many have played, and the frame gaps while one was playing.
  const fxGaps = useRef<number[]>([]);
  const [fx, setFx] = useState({ active: 0, played: 0, p95: 0, worst: 0 });
  // Animated tokens (issue 1176): how many play on screen now, on how many tokens, the capped steps run, and the frame gaps while any played.
  const animGaps = useRef<number[]>([]);
  const [anim, setAnim] = useState({ playing: 0, tokens: 0, steps: 0, p95: 0, worst: 0 });
  const [pyr, setPyr] = useState({ held: 0, asked: 0, level: -1, fetched: 0 });

  useEffect(() => {
    if (!on) return undefined;
    let raf = 0; let last = performance.now(); let live = true;
    const tick = (t: number) => {
      const gap = t - last; last = t;
      gaps.current.push({ at: t, gap });
      while (gaps.current.length && gaps.current[0]!.at < t - WINDOW_MS) gaps.current.shift();
      if (gap > worstRef.current) worstRef.current = gap;
      if (effectsBus.stats().active > 0) { fxGaps.current.push(gap); if (fxGaps.current.length > 2000) fxGaps.current.shift(); }
      if (animationStats.playing > 0) { animGaps.current.push(gap); if (animGaps.current.length > 2000) animGaps.current.shift(); }
      if (live) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const show = setInterval(() => { setStats(frameStats(gaps.current.map((g) => g.gap), WINDOW_MS)); setWorstEver(Math.round(worstRef.current)); const during = frameStats(fxGaps.current, 1000); setFx({ ...effectsBus.stats(), p95: during.p95, worst: during.worst }); const moving = frameStats(animGaps.current, 1000); setAnim({ ...animationStats, p95: moving.p95, worst: moving.worst }); setPyr({ ...pyramidStats }); }, 500);
    return () => { live = false; cancelAnimationFrame(raf); clearInterval(show); };
  }, [on]);

  if (!on) return null;
  const software = isSoftwareRenderer(renderer);
  return (
    <div data-testid="perf-readout" className="pointer-events-auto absolute bottom-2 left-2 z-20 max-w-sm rounded bg-stone-950/90 px-2 py-1 font-mono text-[0.6875rem] leading-4 text-stone-200">
      <div data-testid="perf-frames">{stats.fps} fps · p95 {stats.p95} ms · worst {stats.worst} ms <span className="text-stone-500">(last 5 s)</span></div>
      <div className="flex items-center gap-2">
        <span data-testid="perf-worst">worst since load {worstEver} ms</span>
        <button type="button" className="rounded border border-stone-600 px-1 hover:bg-stone-800" onClick={() => { worstRef.current = 0; setWorstEver(0); fxGaps.current = []; animGaps.current = []; }}>reset</button>
      </div>
      <div data-testid="perf-effects" data-active={fx.active} data-played={fx.played} data-p95={fx.p95} data-worst={fx.worst}>effects {fx.active} playing · {fx.played} played · while playing p95 {fx.p95} ms · worst {fx.worst} ms</div>
      <div data-testid="perf-animated" data-playing={anim.playing} data-tokens={anim.tokens} data-steps={anim.steps} data-p95={anim.p95} data-worst={anim.worst}>animated {anim.playing} playing on {anim.tokens} tokens · {anim.steps} steps · while playing p95 {anim.p95} ms · worst {anim.worst} ms</div>
      {pyr.level >= 0 && <div data-testid="perf-pyramid" data-held={pyr.held} data-level={pyr.level} data-fetched={pyr.fetched}>big map: level {pyr.level} · {pyr.held} tiles held · {pyr.asked} loading · {pyr.fetched} fetched</div>}
      <div data-testid="perf-renderer" className={software ? 'text-amber-300' : 'text-stone-400'}>
        {software ? `Software renderer (${renderer}): these timings are this machine's, not the table's.` : `Renderer: ${renderer}`}
      </div>
    </div>
  );
}
